/**
 * Calculates the completion percentage of infertility section based on patient data
 *
 * Fields counted:
 * - Infertility diagnosis status (yes/no/art_needed)
 * - Causes (if diagnosed with infertility)
 * - Other cause details (if "other" is selected)
 * - Partner infertility status
 * - Partner infertility details (if partner is infertile)
 */

export interface PatientInfertilityHistory {
  isDiagnosedWithInfertility?: boolean | null;
  isARTNeededForCarrierDisease?: boolean | null;
  causes?:
    | {
        cause?: string | null;
        other?: string | null;
      }[]
    | null;
  isPartnerInfertile?: boolean | null;
  partnerInfertilityCause?: string | null;
  notes?: string | null;
}

export interface PatientWithInfertility {
  fertilityHistory?: {
    infertilityHistory?: PatientInfertilityHistory | null;
  } | null;
}

/**
 * Calculate Infertility completion in one pass.
 * Returns: { percent 0..1, label "x/y", completed, total }
 */
type FieldStatus = {
  name: string;
  filled: boolean;
  value: unknown;
  na?: boolean;
};

export function calculateInfertilityCompletion(
  patient: PatientWithInfertility | null | undefined,
): {
  percent: number;
  label: string;
  completed: number;
  total: number;
  breakdown?: {
    filled: FieldStatus[];
    missing: FieldStatus[];
    notApplicable: FieldStatus[];
  };
} {
  const infertilityHistory = patient?.fertilityHistory?.infertilityHistory;

  if (!infertilityHistory) {
    return { percent: 0, label: "0/5", completed: 0, total: 5 };
  }

  const statuses: FieldStatus[] = [];

  // 1. Infertility diagnosis status (at least one must be answered)
  const hasDiagnosisStatus =
    infertilityHistory.isDiagnosedWithInfertility !== undefined &&
    infertilityHistory.isDiagnosedWithInfertility !== null;
  const hasARTStatus =
    infertilityHistory.isARTNeededForCarrierDisease !== undefined &&
    infertilityHistory.isARTNeededForCarrierDisease !== null;

  const hasDiagnosisAnswer = hasDiagnosisStatus || hasARTStatus;
  statuses.push({
    name: "infertilityDiagnosis",
    filled: hasDiagnosisAnswer,
    value: {
      isDiagnosedWithInfertility: infertilityHistory.isDiagnosedWithInfertility,
      isARTNeededForCarrierDisease:
        infertilityHistory.isARTNeededForCarrierDisease,
    },
  });

  // 2. Causes (only required if diagnosed with infertility = true)
  if (infertilityHistory.isDiagnosedWithInfertility === true) {
    const hasCauses =
      infertilityHistory.causes && infertilityHistory.causes.length > 0;
    statuses.push({
      name: "causes",
      filled: !!hasCauses,
      value: infertilityHistory.causes,
    });
  } else {
    // Not diagnosed, causes not applicable
    statuses.push({
      name: "causes",
      filled: true,
      value: null,
      na: true,
    });
  }

  // 3. Other cause details (only required if "other" is in causes)
  const hasOtherCause = infertilityHistory.causes?.some(
    (c) => c.cause === "other",
  );
  if (hasOtherCause) {
    const otherCauseWithText = infertilityHistory.causes?.find(
      (c) => c.cause === "other" && c.other,
    );
    statuses.push({
      name: "otherCauseDetails",
      filled: !!otherCauseWithText,
      value: otherCauseWithText?.other,
    });
  } else {
    // No "other" cause selected, not applicable
    statuses.push({
      name: "otherCauseDetails",
      filled: true,
      value: null,
      na: true,
    });
  }

  // 4. Partner infertility status
  const hasPartnerStatus =
    infertilityHistory.isPartnerInfertile !== undefined &&
    infertilityHistory.isPartnerInfertile !== null;
  statuses.push({
    name: "partnerInfertilityStatus",
    filled: hasPartnerStatus,
    value: infertilityHistory.isPartnerInfertile,
  });

  // 5. Partner infertility details (only required if partner is infertile)
  if (infertilityHistory.isPartnerInfertile === true) {
    const hasPartnerDetails =
      !!infertilityHistory.partnerInfertilityCause ||
      !!infertilityHistory.notes;
    statuses.push({
      name: "partnerDetails",
      filled: hasPartnerDetails,
      value: {
        cause: infertilityHistory.partnerInfertilityCause,
        notes: infertilityHistory.notes,
      },
    });
  } else {
    // Partner not infertile, details not applicable
    statuses.push({
      name: "partnerDetails",
      filled: true,
      value: null,
      na: true,
    });
  }

  // Calculate percentage based on required fields ONLY (exclude n/a)
  const requiredStatuses = statuses.filter((s) => !s.na);
  const completedFields = requiredStatuses.filter((s) => s.filled).length;
  const totalFields = requiredStatuses.length;

  const percent = totalFields > 0 ? completedFields / totalFields : 0;

  const filledList = statuses.filter((s) => s.filled && !s.na);
  const missingList = statuses.filter((s) => !s.filled && !s.na);
  const notApplicableList = statuses.filter((s) => s.na);

  return {
    percent,
    label: `${completedFields}/${totalFields}`,
    completed: completedFields,
    total: totalFields,
    breakdown: {
      filled: filledList,
      missing: missingList,
      notApplicable: notApplicableList,
    },
  };
}
